import { useState, useEffect, useMemo, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@shared/components/ui/dialog";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { supabase } from "@lib/supabase/client";
import { Button } from "@shared/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/components/ui/card";
import { Badge } from "@shared/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@shared/components/ui/avatar";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone, Mail, MessageCircle, Package,
  Clock, ArrowLeft, ExternalLink, UserCircle,
  CheckCircle, Award, Sparkles, Stethoscope, Users, Star, BookOpen,
  ChevronLeft, ChevronRight, Percent
} from "lucide-react";
import { getTemplateById, ClinicTemplate } from "@shared/utils/clinicTemplates";
import { clinicFonts, clinicLanguages } from "@features/clinic/components/ClinicTemplateSelector";
import ClinicLoadingScreen from "@features/clinic/components/ClinicLoadingScreen";
import { getPractitionerLabels } from "@shared/utils/practitionerLabels";
import { readClinicPublicCache, writeClinicPublicCache } from "@features/clinic/lib/clinicPublicCache";
import { useDynamicFavicon } from "@shared/hooks/useDynamicFavicon";

interface Clinic {
  id: string;
  slug: string;
  clinic_name: string;
  clinic_name_ar: string;
  logo_url: string | null;
  cover_image_url: string | null;
  colors: any;
  description: string | null;
  description_ar: string | null;
  specialist_title: string | null;
  specialist_bio: string | null;
  specialist_bio_ar: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  whatsapp_number: string | null;
  social_links: any;
  custom_pages: any;
  template_id: string | null;
  settings: any;
  practitioner_type: string | null;
}

interface Product {
  id: string;
  name: string;
  name_ar: string;
  description: string | null;
  description_ar: string | null;
  image_url: string | null;
  product_type: string;
  price: number;
  consultation_duration_minutes: number;
  subscription_duration_months: number;
  is_active: boolean;
  max_clients: number | null;
  current_clients_count?: number;
  total_slots?: number;
}

interface ClinicBanner {
  id: string;
  image_url: string;
  link_url: string | null;
  button_text: string | null;
  button_url: string | null;
}

interface ClinicCategory {
  id: string;
  name: string;
  image_url: string | null;
  product_ids: string[];
}

interface ClinicPublicCachePayload {
  clinic: Clinic | null;
  products: Product[];
  clinicBanners: ClinicBanner[];
  clinicCategories: ClinicCategory[];
  clinicPromotions: any[];
  isBuilt: boolean | null;
}

interface ClinicNavigationState {
  clinicData?: Clinic | null;
  clinicCache?: ClinicPublicCachePayload | null;
}

const MotionWrapper = ({ children, delay = 0, className = "", id, enabled }: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
  id?: string;
  enabled: boolean;
}) => {
  if (!enabled) return <div className={className} id={id}>{children}</div>;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      className={className}
      id={id}
    >
      {children}
    </motion.div>
  );
};

const ClinicPublicPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationState = location.state as ClinicNavigationState | null;
  const cachedClinicPayload = useMemo(
    () => navigationState?.clinicCache ?? readClinicPublicCache<ClinicPublicCachePayload>(slug),
    [navigationState, slug]
  );

  const [clinic, setClinic] = useState<Clinic | null>(() => navigationState?.clinicData ?? cachedClinicPayload?.clinic ?? null);
  const [products, setProducts] = useState<Product[]>(() => cachedClinicPayload?.products ?? []);
  const [loading, setLoading] = useState(() => !navigationState?.clinicData && !cachedClinicPayload?.clinic);
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [isBuilt, setIsBuilt] = useState<boolean | null>(() => cachedClinicPayload?.isBuilt ?? null);
  const [notFound, setNotFound] = useState(false);
  const [subscriptionExpired] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [clinicBanners, setClinicBanners] = useState<ClinicBanner[]>(() => cachedClinicPayload?.clinicBanners ?? []);
  const [clinicCategories, setClinicCategories] = useState<ClinicCategory[]>(() => cachedClinicPayload?.clinicCategories ?? []);
  const [activeBannerIndex, setActiveBannerIndex] = useState(0);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [clinicPromotions, setClinicPromotions] = useState<any[]>(() => cachedClinicPayload?.clinicPromotions ?? []);

  // Set clinic logo as favicon in browser tab
  useDynamicFavicon(clinic?.logo_url);

  // Set clinic name as the browser tab title, restoring the default title on unmount
  useEffect(() => {
    if (!clinic?.clinic_name_ar) return;
    const previousTitle = document.title;
    document.title = clinic.clinic_name_ar;
    return () => {
      document.title = previousTitle;
    };
  }, [clinic?.clinic_name_ar]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchClinic = async () => {
      try {
        if (!slug) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        const { data: clinicDataArr, error } = await supabase
          .rpc('get_public_clinic_info', { _slug: slug.toLowerCase() });

        const clinicData = clinicDataArr?.[0] || null;

        if (error || !clinicData) {
          console.error('Clinic fetch error:', error);
          if (cachedClinicPayload?.clinic) {
            setLoading(false);
            return;
          }
          setNotFound(true);
          setLoading(false);
          return;
        }

        const { data: builtData } = await supabase
          .from('clinics_public')
          .select('is_built')
          .eq('slug', slug.toLowerCase())
          .maybeSingle();

        const clinicIsBuilt = builtData?.is_built ?? false;
        setIsBuilt(clinicIsBuilt);

        if (!clinicIsBuilt) {
          setShowLoadingScreen(true);
        }

        setClinic(clinicData);

        let resolvedProducts: Product[] = [];
        let resolvedBanners: ClinicBanner[] = [];
        let resolvedCategories: ClinicCategory[] = [];
        let resolvedPromotions: any[] = [];

        try {
          const { data: productsData } = await supabase
            .from('clinic_products')
            .select('*')
            .eq('clinic_id', clinicData.id)
            .eq('is_active', true)
            .order('display_order');

          if (productsData && productsData.length > 0) {
            const now = new Date();
            const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
            const startOfMonth = `${currentMonth}-01`;

            resolvedProducts = await Promise.all(
              productsData.map(async (product) => {
                try {
                  if (product.max_clients) {
                    const { count } = await supabase
                      .from('clinic_clients')
                      .select('*', { count: 'exact', head: true })
                      .eq('clinic_id', clinicData.id)
                      .eq('product_id', product.id)
                      .in('status', ['approved', 'pending'])
                      .gte('created_at', startOfMonth);

                    const bonusSlots = product.bonus_slots_month === currentMonth
                      ? (product.bonus_slots || 0)
                      : 0;
                    const totalSlots = product.max_clients + bonusSlots;

                    return {
                      ...product,
                      current_clients_count: count || 0,
                      total_slots: totalSlots
                    };
                  }
                  return { ...product, current_clients_count: 0, total_slots: 0 };
                } catch (productError) {
                  console.error('Product count error:', productError);
                  return { ...product, current_clients_count: 0, total_slots: 0 };
                }
              })
            );
          }

          setProducts(resolvedProducts);
        } catch (productsError) {
          console.error('Products fetch error:', productsError);
        }

        try {
          const [bannersRes, categoriesRes] = await Promise.all([
            supabase.from('clinic_banners').select('id, image_url, link_url, button_text, button_url').eq('clinic_id', clinicData.id).eq('is_active', true).order('display_order'),
            supabase.from('clinic_categories').select('id, name, image_url').eq('clinic_id', clinicData.id).eq('is_active', true).order('display_order'),
          ]);

          resolvedBanners = bannersRes.data || [];
          setClinicBanners(resolvedBanners);

          if (categoriesRes.data && categoriesRes.data.length > 0) {
            const catIds = categoriesRes.data.map(c => c.id);
            const { data: catProducts } = await supabase
              .from('clinic_category_products')
              .select('category_id, product_id')
              .in('category_id', catIds);

            resolvedCategories = categoriesRes.data.map(cat => ({
              ...cat,
              product_ids: (catProducts || []).filter(cp => cp.category_id === cat.id).map(cp => cp.product_id),
            }));
          }

          setClinicCategories(resolvedCategories);
        } catch (e) {
          console.error('Banners/categories fetch error:', e);
        }

        try {
          const { data: promosData } = await supabase
            .from('clinic_promotions')
            .select('*')
            .eq('clinic_id', clinicData.id)
            .eq('is_active', true)
            .lte('starts_at', new Date().toISOString());

          if (promosData) {
            resolvedPromotions = promosData.filter(p => {
              if (p.expires_at && new Date(p.expires_at) < new Date()) return false;
              if (p.max_uses && p.used_count >= p.max_uses) return false;
              return true;
            });
          }

          setClinicPromotions(resolvedPromotions);
        } catch (e) {
          console.error('Promotions fetch error:', e);
        }

        writeClinicPublicCache<ClinicPublicCachePayload>(slug, {
          clinic: clinicData,
          products: resolvedProducts,
          clinicBanners: resolvedBanners,
          clinicCategories: resolvedCategories,
          clinicPromotions: resolvedPromotions,
          isBuilt: clinicIsBuilt,
        });

        setLoading(false);
      } catch (err) {
        console.error('Clinic page error:', err);
        if (cachedClinicPayload?.clinic) {
          setLoading(false);
          return;
        }
        setNotFound(true);
        setLoading(false);
      }
    };

    fetchClinic();
  }, [cachedClinicPayload?.clinic, slug]);

  // Get template and colors
  const template = useMemo(() => {
    return getTemplateById(clinic?.template_id || 'classic-medical') || getTemplateById('classic-medical')!;
  }, [clinic?.template_id]);

  // Get selected font
  const selectedFont = useMemo(() => {
    const fontId = clinic?.settings?.font || 'tajawal';
    return clinicFonts.find(f => f.id === fontId) || clinicFonts[0];
  }, [clinic?.settings?.font]);

  // Load Google Font dynamically
  useEffect(() => {
    if (selectedFont?.googleFont) {
      const linkId = 'clinic-google-font';
      let link = document.getElementById(linkId) as HTMLLinkElement | null;
      
      if (!link) {
        link = document.createElement('link');
        link.id = linkId;
        link.rel = 'stylesheet';
        document.head.appendChild(link);
      }
      
      link.href = `https://fonts.googleapis.com/css2?family=${selectedFont.googleFont}&display=swap`;
    }
  }, [selectedFont?.googleFont]);

  // Get selected language and direction
  const selectedLanguage = useMemo(() => {
    const langId = clinic?.settings?.language || 'ar';
    return clinicLanguages.find(l => l.id === langId) || clinicLanguages[0];
  }, [clinic?.settings?.language]);

  const isRTL = selectedLanguage.direction === 'rtl';
  const isArabic = selectedLanguage.id === 'ar';

  const primaryColor = clinic?.colors?.primary || '#8a8a8a';
  

  // Helper function to convert hex to RGB
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 138, g: 138, b: 138 };
  };

  // Helper to create a light tinted background from primary color
  const getLightTintedBackground = (hexColor: string, opacity: number = 0.08) => {
    const rgb = hexToRgb(hexColor);
    // Blend with white to create a very light tint
    const blendWithWhite = (value: number, factor: number) => Math.round(255 - (255 - value) * factor);
    const r = blendWithWhite(rgb.r, opacity);
    const g = blendWithWhite(rgb.g, opacity);
    const b = blendWithWhite(rgb.b, opacity);
    return `rgb(${r}, ${g}, ${b})`;
  };

  // Get theme colors - Apply specialist's chosen colors with appropriate background tinting
  const getThemeColors = (template: ClinicTemplate) => {
    const isDark = template.styles.theme === 'dark';
    
    // Calculate text color based on theme brightness
    const getTextColor = () => {
      if (isDark) return '#f9fafb';
      return '#1f2937';
    };
    
    // Generate background color from primary with high transparency (like the preview)
    const getBgColor = () => {
      if (isDark) return '#111827';
      // Create a very light tint of the primary color (8-10% opacity effect)
      return getLightTintedBackground(primaryColor, 0.1);
    };
    
    return { 
      bg: getBgColor(), 
      header: primaryColor, 
      accent: primaryColor,
      
      text: getTextColor() 
    };
  };

  // Template-based styles
  const templateStyles = useMemo(() => {
    if (!template) return {
      isDark: false,
      themeColors: { bg: '#ffffff', header: '#8a8a8a', accent: '#8a8a8a', text: '#1f2937' },
      bgColor: '#ffffff',
      headerHeight: 'h-52 md:h-60',
      cardClasses: 'bg-white shadow-lg border-0 rounded-lg overflow-hidden',
      useAnimations: true,
      accentColor: '#8a8a8a',
      
    };
    
    const themeColors = getThemeColors(template);
    const isDark = template.styles.theme === 'dark';

    // Profile card offset should remain consistent regardless of header height
    // We keep the same offset so the profile card appears at a fixed position from top
    // Profile offset matching the preview proportions (~40% of header height)
    // Profile offset matching the preview exactly
    const getProfileOffset = () => {
      switch (template.styles.headerHeight) {
        case 'hero': return '-mt-44';
        case 'tall': return '-mt-40';
        case 'short': return '-mt-20';
        default: return '-mt-36';
      }
    };

    return {
      isDark,
      themeColors,
      bgColor: themeColors.bg,
      headerHeight: template.styles.headerHeight === 'hero' ? 'h-72 md:h-80' :
        template.styles.headerHeight === 'tall' ? 'h-52 md:h-60' : 
        template.styles.headerHeight === 'short' ? 'h-28 md:h-36' : 'h-36 md:h-44',
      profileOffset: getProfileOffset(),
      cardClasses: getCardClasses(template, isDark),
      useAnimations: template.styles.animations !== 'none',
      accentColor: themeColors.accent,
      
    };
  }, [template, primaryColor]);

  function getCardClasses(template: ClinicTemplate, isDark: boolean): string {
    let classes = 'overflow-hidden ';
    
    switch (template.styles.cardStyle) {
      case 'elevated':
        classes += 'bg-white shadow-lg border-0 ';
        break;
      case 'flat':
        classes += isDark ? 'bg-gray-800 border-gray-700 ' : 'bg-gray-50 border-0 ';
        break;
      case 'bordered':
        classes += `${isDark ? 'bg-gray-800' : 'bg-white'} border ${isDark ? 'border-gray-700' : 'border-gray-200'} `;
        break;
      case 'glass':
        classes += 'bg-white/80 backdrop-blur-sm border border-white/20 shadow-lg ';
        break;
      case 'floating':
        classes += 'bg-white shadow-xl border-0 ';
        break;
      case 'minimal':
        classes += `${isDark ? 'bg-transparent' : 'bg-white'} border-b ${isDark ? 'border-gray-700' : 'border-gray-100'} shadow-sm `;
        break;
      default:
        classes += 'bg-white shadow-lg border-0 ';
    }

    switch (template.styles.cardRadius) {
      case 'none': classes += 'rounded-none'; break;
      case 'small': classes += 'rounded-md'; break;
      case 'medium': classes += 'rounded-lg'; break;
      case 'large': classes += 'rounded-xl'; break;
      case 'full': classes += 'rounded-2xl'; break;
      default: classes += 'rounded-lg';
    }
    
    return classes;
  }

  const getHeaderBackground = () => {
    const themeColors = getThemeColors(template);
    
    if (template.styles.profilePosition === 'top-bar') {
      const headerImage = clinic?.cover_image_url || '/clinic-defaults/corporate-header.jpg?v=2';
      return { 
        backgroundImage: `url(${headerImage})`,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat'
      };
    }
    
    // Different header styles
    switch (template.styles.headerStyle) {
      case 'gradient':
        return { background: `linear-gradient(135deg, ${themeColors.header} 0%, ${themeColors.header}dd 100%)` };
      case 'solid':
        return { background: themeColors.header };
      case 'minimal':
        return { background: `${themeColors.header}20` };
      case 'split':
        return { background: `linear-gradient(to left, ${themeColors.header} 60%, ${themeColors.header}cc 100%)` };
      case 'wave':
        return { background: themeColors.header };
      case 'full-image':
        return { background: `linear-gradient(135deg, ${themeColors.header} 0%, ${themeColors.header}ee 100%)` };
      default:
        return { background: themeColors.header };
    }
  };

  const handleBookProduct = (product: Product) => {
    const discountInfo = getProductDiscount(product);
    navigate(`/clinic/${slug}/book/${product.id}`, {
      state: {
        appliedDiscountCode: null,
        discountedPrice: discountInfo?.discountedPrice ?? null,
      },
    });
  };

  // Apply font to CSS variable for all elements
  useEffect(() => {
    document.documentElement.style.setProperty('--clinic-font', selectedFont.fontFamily);
    return () => {
      document.documentElement.style.removeProperty('--clinic-font');
    };
  }, [selectedFont.fontFamily]);

  // Handle loading screen complete - mark clinic as built
  const handleLoadingComplete = useCallback(async () => {
    setShowLoadingScreen(false);
    setIsBuilt(true);
    // Mark clinic as built in database
    if (slug) {
      await supabase
        .from('clinics')
        .update({ is_built: true })
        .eq('slug', slug.toLowerCase());
    }
  }, [slug]);

  // Banner auto-rotation removed - user controls sliding manually

  // Filter products by selected category
  const practitionerLabels = useMemo(() => getPractitionerLabels(clinic?.practitioner_type), [clinic?.practitioner_type]);

  // Calculate discounted price for a product based on active promotions
  const getProductDiscount = useCallback((product: Product) => {
    if (!product.price || product.product_type === 'free_consultation') return null;
    
    let bestDiscount = 0;
    let discountLabel = "";
    
    for (const promo of clinicPromotions) {
      // Discount-code promos are only entered and applied during booking
      // (ClinicBooking.tsx), not previewed here on the listing page.
      if (promo.promotion_type === 'discount_code') continue;

      // Check if promo applies to this product
      if (promo.promotion_type === 'product_discount') {
        if (!promo.applicable_product_ids?.includes(product.id)) continue;
      }
      
      // Skip bundle type (handled separately)
      if (promo.promotion_type === 'bundle') continue;
      
      // Calculate discount amount
      let discountAmount = 0;
      if (promo.discount_type === 'percentage') {
        discountAmount = product.price * (promo.discount_value / 100);
      } else {
        discountAmount = promo.discount_value;
      }
      
      if (discountAmount > bestDiscount) {
        bestDiscount = discountAmount;
        discountLabel = promo.discount_type === 'percentage' 
          ? `${promo.discount_value}%` 
          : `${promo.discount_value} ر.س`;
      }
    }
    
    if (bestDiscount <= 0) return null;
    
    const discountedPrice = Math.max(0, product.price - bestDiscount);
    // Find the promo name for the ribbon
    const matchingPromo = clinicPromotions.find(p => {
      if (p.promotion_type === 'discount_code') return false;
      if (p.promotion_type === 'product_discount' && !p.applicable_product_ids?.includes(product.id)) return false;
      if (p.promotion_type === 'bundle') return false;
      return true;
    });
    return { originalPrice: product.price, discountedPrice, discountLabel, promoName: matchingPromo?.name || '' };
  }, [clinicPromotions]);

  // Check for active global/product discount banner
  const clinicRouteState = clinic ? {
    clinicData: clinic,
    clinicCache: {
      clinic,
      products,
      clinicBanners,
      clinicCategories,
      clinicPromotions,
      isBuilt,
    }
  } : undefined;
  const { isDark, bgColor, accentColor, useAnimations, profileOffset } = templateStyles;


  // Show loading screen for first-time build, or just wait for data
  if (showLoadingScreen) {
    return (
      <AnimatePresence>
        <ClinicLoadingScreen onComplete={!loading ? handleLoadingComplete : undefined} />
      </AnimatePresence>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: templateStyles.bgColor }}>
        <div className="animate-pulse">
          <div className="h-52 md:h-60 w-full bg-muted" />
          <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
            <div className="h-40 bg-muted rounded-lg" />
            <div className="h-24 bg-muted rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  if (subscriptionExpired && clinic) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white" dir="rtl">
        <Card className="w-full max-w-md mx-4 border-0 shadow-xl">
          <CardContent className="pt-8 pb-8 text-center">
            {clinic.logo_url && (
              <img src={clinic.logo_url} alt={clinic.clinic_name_ar} className="h-16 w-16 object-contain mx-auto mb-4 rounded-full" />
            )}
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <Clock className="h-8 w-8 text-muted-foreground" />
            </div>
            <h1 className="text-2xl font-bold mb-2">العيادة الإلكترونية قيد الصيانة</h1>
            <p className="text-muted-foreground mb-2 font-semibold">
              {clinic.clinic_name_ar}
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              عذراً، العيادة الإلكترونية قيد الصيانة حالياً. نعمل على تحسين الخدمة وسنعود قريباً. شكراً لتفهمكم.
            </p>
            <Button onClick={() => navigate('/')} variant="outline" className="px-8">
              العودة للرئيسية
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (notFound || !clinic) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white" dir="rtl">
        <Card className="w-full max-w-md mx-4 border-0 shadow-xl">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <ExternalLink className="h-8 w-8 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold mb-2">العيادة غير موجودة</h1>
            <p className="text-muted-foreground mb-6">
              عذراً، لم نتمكن من إيجاد العيادة المطلوبة
            </p>
            <Button onClick={() => navigate('/')} className="px-8">
              العودة للرئيسية
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen w-full [&_*]:font-[inherit]" 
      style={{ backgroundColor: bgColor, fontFamily: selectedFont.fontFamily }} 
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Top Navbar for corporate/top-bar template */}
      {template.styles.profilePosition === 'top-bar' && (
        <div 
          className={`w-full px-4 md:px-8 lg:px-16 xl:px-24 py-3 flex items-center justify-between fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
            isScrolled 
              ? 'shadow-sm' 
              : ''
          }`}
          style={{ 
            backgroundColor: isScrolled ? (isDark ? '#111827' : '#ffffff') : 'transparent',
            borderBottom: isScrolled ? `1px solid ${isDark ? '#374151' : '#e5e7eb'}` : '1px solid transparent'
          }}
        >
          {/* Right side - Logo + Clinic Name */}
          <div className="flex items-center gap-3">
            {clinic.logo_url ? (
              <img src={clinic.logo_url} alt={clinic.clinic_name_ar} className="w-10 h-10 md:w-12 md:h-12 rounded-lg object-contain" />
            ) : (
              <div 
                className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center text-white text-lg font-bold"
                style={{ backgroundColor: accentColor }}
              >
                {clinic.clinic_name_ar?.charAt(0) || clinic.clinic_name?.charAt(0)}
              </div>
            )}
            <div>
              <h1 className={`font-bold text-base md:text-lg transition-colors duration-300 ${isScrolled ? (isDark ? 'text-white' : 'text-foreground') : 'text-white'}`}>{clinic.clinic_name_ar}</h1>
              {clinic.specialist_title && (
                <p className={`text-xs md:text-sm transition-colors duration-300 ${isScrolled ? (isDark ? 'text-gray-400' : 'text-muted-foreground') : 'text-white/80'}`}>{clinic.specialist_title}</p>
              )}
            </div>
          </div>

          {/* Left side - Portal Button + Contact Buttons */}
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => navigate(`/clinic/${slug}/portal`)}
              className="gap-2 text-white text-sm"
              style={{ backgroundColor: accentColor }}
            >
              <UserCircle className="h-4 w-4" />
              <span className="hidden sm:inline">{isArabic ? 'بوابة المراجع' : 'Client Portal'}</span>
            </Button>
            <div className="flex gap-1.5">
              {clinic.whatsapp_number && clinic.settings?.show_whatsapp !== false && (
                <Button
                  variant="outline"
                  size="icon"
                  className={`w-9 h-9 transition-colors duration-300 ${!isScrolled ? 'border-white/40 bg-white/10 hover:bg-white/20' : 'hover:bg-muted'}`}
                  onClick={() => window.open(`https://wa.me/${clinic.whatsapp_number}`, '_blank')}
                >
                  <MessageCircle className={`h-4 w-4 ${!isScrolled ? 'text-white' : 'text-green-600'}`} />
                </Button>
              )}
              {clinic.contact_phone && clinic.settings?.show_phone !== false && (
                <Button
                  variant="outline"
                  size="icon"
                  className={`w-9 h-9 transition-colors duration-300 ${!isScrolled ? 'border-white/40 bg-white/10 hover:bg-white/20' : 'hover:bg-muted'}`}
                  onClick={() => window.open(`tel:${clinic.contact_phone}`, '_blank')}
                >
                  <Phone className={`h-4 w-4 ${!isScrolled ? 'text-white' : ''}`} style={isScrolled ? { color: accentColor } : undefined} />
                </Button>
              )}
              {clinic.contact_email && clinic.settings?.show_email !== false && (
                <Button
                  variant="outline"
                  size="icon"
                  className={`w-9 h-9 transition-colors duration-300 ${!isScrolled ? 'border-white/40 bg-white/10 hover:bg-white/20' : 'hover:bg-muted'}`}
                  onClick={() => window.open(`mailto:${clinic.contact_email}`, '_blank')}
                >
                  <Mail className={`h-4 w-4 ${!isScrolled ? 'text-white' : ''}`} style={isScrolled ? { color: accentColor } : undefined} />
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hero Section with Cover */}
      <div className="relative">
        <div 
          className={`${template.styles.profilePosition === 'top-bar' ? 'min-h-[60vh] md:min-h-[70vh] lg:min-h-[80vh]' : templateStyles.headerHeight} relative overflow-hidden`}
          style={getHeaderBackground()}
        >
          {/* Cover image with color overlay for top-bar template */}
          {template.styles.profilePosition === 'top-bar' && (
            <div 
              className="absolute inset-0" 
              style={{ 
                backgroundColor: `${accentColor}99`,
                mixBlendMode: clinic.cover_image_url ? 'normal' : 'normal'
              }} 
            />
          )}
          
          {/* Header Overlay based on template */}
          {template.styles.headerOverlay === 'gradient' && template.styles.profilePosition !== 'top-bar' && (
              <div 
                className="absolute inset-0" 
                style={{ 
                  background: `linear-gradient(to bottom, transparent 0%, transparent 50%, ${bgColor} 100%)` 
                }} 
              />
          )}
          {template.styles.headerOverlay === 'dark' && template.styles.profilePosition !== 'top-bar' && (
            <div className="absolute inset-0 bg-black/30" />
          )}
          {template.styles.headerOverlay === 'light' && (
            <div className="absolute inset-0 bg-black/10" />
          )}
          
          {/* Bottom gradient fade for top-bar template */}
          {template.styles.profilePosition === 'top-bar' && (
            <div 
              className="absolute bottom-0 left-0 right-0 h-24" 
              style={{ 
                background: `linear-gradient(to top, ${bgColor} 0%, transparent 100%)` 
              }} 
            />
          )}
          
          {/* Decorations */}
          {template.styles.decorations === 'waves' && (
            <div className="absolute bottom-0 left-0 right-0 h-12">
              <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-full">
                <path d="M0,60 C300,100 600,20 900,60 C1050,80 1150,40 1200,60 L1200,120 L0,120 Z" 
                  fill={bgColor} />
              </svg>
            </div>
          )}
          {template.styles.decorations === 'organic-shapes' && (
            <>
              <div className="absolute top-8 right-8 w-16 h-16 sm:w-32 sm:h-32 rounded-full bg-white/10" />
              <div className="hidden sm:block absolute bottom-16 left-12 w-48 h-48 rounded-full bg-white/5" />
            </>
          )}
          {template.styles.decorations === 'medical-icons' && (
            <div className="absolute top-8 left-8 opacity-20">
              <Stethoscope className="w-16 h-16 text-white" />
            </div>
          )}
        </div>

        <div className="w-full px-4 md:px-8 lg:px-16 xl:px-24">
          {/* Sidebar Layout (for wellness-warm template) */}
          {template.styles.profilePosition === 'sidebar' ? (
            <MotionWrapper enabled={useAnimations} delay={0} className={`${profileOffset} relative z-10`}>
              <div className="flex flex-col md:flex-row gap-6">
                {/* Sidebar Profile Card */}
                <div 
                  className="md:w-1/3 shrink-0 p-4 rounded-2xl flex flex-col items-center justify-center"
                  style={{ 
                    backgroundColor: getLightTintedBackground(accentColor, 0.12), 
                    boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                    border: 'none'
                  }}
                >
                  {/* ... keep existing code (sidebar profile content) */}
                  <div className="text-center">
                    {/* Avatar */}
                    <div className="relative inline-block mb-3">
                      <Avatar className={`w-32 h-32 border-4 shadow-lg ${isDark ? 'border-gray-700' : 'border-white'}`}>
                        <AvatarImage src={clinic.logo_url || undefined} className="object-contain bg-white p-1" />
                        <AvatarFallback 
                          className="text-4xl text-white font-bold"
                          style={{ backgroundColor: accentColor }}
                        >
                          {clinic.clinic_name_ar?.charAt(0) || clinic.clinic_name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div 
                        className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center shadow-lg"
                        style={{ backgroundColor: accentColor }}
                      >
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                    </div>
                    
                    {/* Name & Title */}
                    <h3 className={`font-bold text-lg mb-1 ${isDark ? 'text-white' : ''}`}>
                      {clinic.clinic_name_ar}
                    </h3>
                    {clinic.specialist_title && (
                      <p className={`text-base mb-3 ${isDark ? 'text-gray-400' : 'text-muted-foreground'}`}>
                        {clinic.specialist_title}
                      </p>
                    )}
                    
                    {/* Contact Icons */}
                    <div className="flex justify-center gap-2">
                      {clinic.contact_phone && clinic.settings?.show_phone !== false && (
                        <Button
                          variant="outline"
                          size="icon"
                          className={`w-9 h-9 hover:bg-muted ${isDark ? 'border-gray-600' : ''}`}
                          style={{ borderColor: `${accentColor}30` }}
                          onClick={() => window.open(`tel:${clinic.contact_phone}`, '_blank')}
                        >
                          <Phone className="h-4 w-4" style={{ color: accentColor }} />
                        </Button>
                      )}
                      {clinic.contact_email && clinic.settings?.show_email !== false && (
                        <Button
                          variant="outline"
                          size="icon"
                          className={`w-9 h-9 hover:bg-muted ${isDark ? 'border-gray-600' : ''}`}
                          style={{ borderColor: `${accentColor}30` }}
                          onClick={() => window.open(`mailto:${clinic.contact_email}`, '_blank')}
                        >
                          <Mail className="h-4 w-4" style={{ color: accentColor }} />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Main Content - About & Stats */}
                <div className="flex-1 space-y-4">
                  {(clinic.description_ar || clinic.specialist_bio_ar) && (
                  <div 
                    className="p-4 rounded-2xl"
                    style={{ 
                      backgroundColor: isDark ? '#1f2937' : '#ffffff',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                      border: 'none'
                    }}
                  >
                    {clinic.description_ar && (
                      <>
                        <h4 className={`font-bold text-lg mb-2 flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                          <BookOpen className="w-5 h-5" style={{ color: accentColor }} />
                          {isArabic ? 'نبذة عنّا' : 'About Us'}
                        </h4>
                        <p className={`text-base leading-relaxed ${isDark ? 'text-gray-300' : 'text-muted-foreground'}`}>
                          {clinic.description_ar}
                        </p>
                      </>
                    )}
                    {clinic.specialist_bio_ar && (
                      <div className={clinic.description_ar ? `pt-4 mt-4 ${isDark ? 'border-gray-700' : ''} border-t` : ''}>
                        <h4 className={`font-bold text-lg mb-2 flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                          <Award className="w-5 h-5" style={{ color: accentColor }} />
                          {isArabic ? practitionerLabels.aboutLabel : practitionerLabels.aboutLabelEn}
                        </h4>
                        <p className={`text-base leading-relaxed ${isDark ? 'text-gray-300' : 'text-muted-foreground'}`}>
                          {clinic.specialist_bio_ar}
                        </p>
                      </div>
                    )}
                  </div>
                  )}
                  
                  {/* Stats Section */}
                  {(clinic?.settings?.show_clinic_stats ?? true) && (
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { icon: Star, value: clinic?.settings?.stats_rating || '0', label: 'تقييم' },
                      { icon: Award, value: clinic?.settings?.stats_experience || '0', label: 'سنوات خبرة' },
                      { icon: Users, value: clinic?.settings?.stats_clients || '0', label: 'عميل' }
                    ].map((stat, i) => (
                      <div
                        key={i}
                        className="text-center p-3 md:p-5 rounded-xl"
                        style={{
                          backgroundColor: getLightTintedBackground(accentColor, 0.08),
                          border: `1px solid ${accentColor}20`
                        }}
                      >
                        <stat.icon className="w-5 h-5 mx-auto mb-1.5" style={{ color: accentColor }} />
                        <div className="font-bold text-lg" style={{ color: accentColor }}>{stat.value}</div>
                        <div className={`text-sm font-medium mt-1 ${isDark ? 'text-gray-400' : 'text-muted-foreground'}`}>{stat.label}</div>
                      </div>
                    ))}
                  </div>
                  )}

                  {/* Portal Button */}
                  <Button 
                    onClick={() => navigate(`/clinic/${slug}/portal`)}
                    className="w-full gap-2 text-white"
                    style={{ backgroundColor: accentColor }}
                  >
                    <UserCircle className="h-4 w-4" />
                    {isArabic ? 'بوابة المراجعين' : 'Client Portal'}
                  </Button>
                </div>
              </div>
            </MotionWrapper>
          ) : template.styles.profilePosition === 'top-bar' ? (
            /* Top-bar layout - no profile card needed, it's in the navbar */
            null
          ) : (
            /* Standard Layout (floating, inline, etc.) */
            <MotionWrapper enabled={useAnimations} delay={0} className={template.styles.profilePosition === 'floating' ? `${profileOffset} relative z-10` : 'mt-6'}>
              <Card className={templateStyles.cardClasses}>
                <CardContent className="p-6 md:p-8">
                  <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                    {/* Avatar */}
                    <div className="relative">
                      <div 
                        className="rounded-full p-1"
                        style={{ backgroundColor: `${accentColor}20` }}
                      >
                        <Avatar className={`w-24 h-24 md:w-28 md:h-28 border-4 shadow-lg ${isDark ? 'border-gray-700' : 'border-white'}`}>
                          <AvatarImage src={clinic.logo_url || undefined} className="object-cover" />
                          <AvatarFallback 
                            className="text-3xl text-white font-bold"
                            style={{ backgroundColor: accentColor }}
                          >
                            {clinic.clinic_name_ar?.charAt(0) || clinic.clinic_name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                      <div 
                        className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center shadow-lg"
                        style={{ backgroundColor: accentColor }}
                      >
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                    </div>

                    {/* Info */}
                    <div className={`flex-1 text-center md:text-right ${isDark ? 'text-white' : ''}`}>
                      <h1 className="text-xl md:text-2xl font-bold mb-2">
                        {clinic.clinic_name_ar}
                      </h1>
                      {clinic.specialist_title && (
                        <div className="flex items-center justify-center md:justify-start gap-2 mb-3">
                          <Award className="w-4 h-4" style={{ color: accentColor }} />
                          <span className={isDark ? 'text-gray-300' : 'text-muted-foreground'}>{clinic.specialist_title}</span>
                        </div>
                      )}
                      
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row md:flex-col gap-2 w-full md:w-auto">
                      <Button 
                        onClick={() => navigate(`/clinic/${slug}/portal`)}
                        className="gap-2 text-white"
                        style={{ backgroundColor: accentColor }}
                      >
                        <UserCircle className="h-4 w-4" />
                        {isArabic ? 'بوابة المراجع' : 'Client Portal'}
                      </Button>
                      <div className="flex gap-2 justify-center">
                        {clinic.whatsapp_number && clinic.settings?.show_whatsapp !== false && (
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => window.open(`https://wa.me/${clinic.whatsapp_number}`, '_blank')}
                            className={`hover:bg-green-50 hover:border-green-300 ${isDark ? 'border-gray-600 hover:border-green-500' : ''}`}
                          >
                            <MessageCircle className="h-5 w-5 text-green-600" />
                          </Button>
                        )}
                        {clinic.contact_phone && clinic.settings?.show_phone !== false && (
                          <Button
                            variant="outline"
                            size="icon"
                            className={isDark ? 'border-gray-600' : ''}
                            onClick={() => window.open(`tel:${clinic.contact_phone}`, '_blank')}
                          >
                            <Phone className="h-5 w-5" style={{ color: accentColor }} />
                          </Button>
                        )}
                        {clinic.contact_email && clinic.settings?.show_email !== false && (
                          <Button
                            variant="outline"
                            size="icon"
                            className={isDark ? 'border-gray-600' : ''}
                            onClick={() => window.open(`mailto:${clinic.contact_email}`, '_blank')}
                          >
                            <Mail className="h-5 w-5" style={{ color: accentColor }} />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </MotionWrapper>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full px-4 md:px-8 lg:px-16 xl:px-24 py-8 md:py-12">
        {/* About Section */}
        {/* Skip about section for sidebar templates since it's already in the sidebar area */}
        {template.styles.profilePosition !== 'sidebar' && (clinic.description_ar || clinic.specialist_bio_ar) && (
          <MotionWrapper enabled={useAnimations} delay={0.2} className="mb-10">
            <div 
              className={`overflow-hidden rounded-lg border ${isDark ? 'bg-card border-border' : 'bg-white border-border/50'}`}
            >
            <Card className="bg-transparent border-0 shadow-none">
              <CardHeader className="pb-2">
                <CardTitle className={`text-xl flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${accentColor}15` }}
                  >
                    <BookOpen className="w-4 h-4" style={{ color: accentColor }} />
                  </div>
                  {isArabic ? 'نبذة عنّا' : 'About Us'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {clinic.description_ar && (
                  <p className={`leading-relaxed ${isDark ? 'text-gray-300' : 'text-muted-foreground'}`}>{clinic.description_ar}</p>
                )}
                {clinic.specialist_bio_ar && (
                  <div className={`pt-4 ${isDark ? 'border-gray-700' : ''} border-t`}>
                    <h4 className={`font-bold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                      <Award className="w-4 h-4" style={{ color: accentColor }} />
                      {isArabic ? practitionerLabels.aboutLabel : practitionerLabels.aboutLabelEn}
                    </h4>
                    <p className={`leading-relaxed ${isDark ? 'text-gray-300' : 'text-muted-foreground'}`}>{clinic.specialist_bio_ar}</p>
                  </div>
                )}
              </CardContent>
            </Card>
            </div>
          </MotionWrapper>
        )}

        {/* Stats Section for Modern Teal template */}
        {template.styles.effects.includes('stats-counter') && (clinic?.settings?.show_clinic_stats ?? true) && (
          <MotionWrapper enabled={useAnimations} delay={0.25} className="mb-10">
            <div className="grid grid-cols-3 py-6 border-b" style={{ borderColor: isDark ? '#374151' : '#e5e7eb' }}>
              {[
                { val: clinic?.settings?.stats_clients || '0', label: 'مراجع' },
                { val: clinic?.settings?.stats_experience || '0', label: 'سنوات خبرة' },
                { val: clinic?.settings?.stats_rating || '0', label: 'تقييم' }
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-xl md:text-3xl font-bold" style={{ color: accentColor }}>{stat.val}</div>
                  <div className={`text-xs md:text-sm ${isDark ? 'text-gray-400' : 'text-muted-foreground'}`}>{stat.label}</div>
                </div>
              ))}
            </div>
          </MotionWrapper>
        )}

        {/* Banners Carousel */}
        {clinicBanners.length > 0 && (
          <MotionWrapper enabled={useAnimations} delay={0.25} className="mb-10">
            <div className="relative rounded-2xl overflow-hidden h-36 sm:h-48 md:aspect-[3/1] md:h-auto">
              <AnimatePresence initial={false}>
                <motion.div
                  key={activeBannerIndex}
                  className="absolute inset-0"
                  initial={{ x: '-100%' }}
                  animate={{ x: 0 }}
                  exit={{ x: '100%' }}
                  transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                >
                  <img
                    src={clinicBanners[activeBannerIndex].image_url}
                    alt={`بانر ${activeBannerIndex + 1}`}
                    className="w-full h-full object-cover cursor-pointer"
                    onClick={() => {
                      const link = clinicBanners[activeBannerIndex].link_url;
                      if (link) window.open(link, '_blank');
                    }}
                  />
                  {clinicBanners[activeBannerIndex].button_text && (
                    <div className="absolute inset-0 flex items-end justify-center pb-4 sm:pb-12">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const url = clinicBanners[activeBannerIndex].button_url?.trim();

                          if (url?.startsWith('#booking')) {
                            const productId = url.split(':')[1];
                            const product = productId ? products.find((p) => p.id === productId) : null;

                            if (product) {
                              handleBookProduct(product);
                              return;
                            }

                            const productsSection = document.getElementById('products');
                            if (productsSection) {
                              productsSection.scrollIntoView({ behavior: 'smooth' });
                            }
                            return;
                          }

                          if (url?.startsWith('#')) {
                            const el = document.getElementById(url.slice(1));
                            if (el) el.scrollIntoView({ behavior: 'smooth' });
                            return;
                          }

                          if (url) {
                            window.open(url, '_blank');
                          }
                        }}
                        className="px-8 py-3 rounded-full text-white font-bold text-sm shadow-lg hover:scale-105 transition-transform"
                        style={{ backgroundColor: primaryColor || 'hsl(var(--primary))' }}
                      >
                        {clinicBanners[activeBannerIndex].button_text}
                      </button>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
              {clinicBanners.length > 1 && (
                <>
                  <button
                    onClick={() => setActiveBannerIndex(prev => (prev - 1 + clinicBanners.length) % clinicBanners.length)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/30 text-white flex items-center justify-center hover:bg-black/50 transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setActiveBannerIndex(prev => (prev + 1) % clinicBanners.length)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/30 text-white flex items-center justify-center hover:bg-black/50 transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                    {clinicBanners.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setActiveBannerIndex(i)}
                        className={`w-2.5 h-2.5 rounded-full transition-colors ${i === activeBannerIndex ? 'bg-white' : 'bg-white/50'}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </MotionWrapper>
        )}

        {/* Categories Section */}
        {clinicCategories.length > 0 && (
          <MotionWrapper enabled={useAnimations} delay={0.28} className="mb-10">
            <h3 className={`font-bold text-xl mb-6 text-center ${isDark ? 'text-white' : ''}`}>
              {isArabic ? 'التصنيفات' : 'Categories'}
            </h3>
            <div className="flex flex-wrap justify-center gap-4">
              {/* All button */}
              <button
                onClick={() => {
                  setSelectedCategoryId(null);
                  const el = document.getElementById('products');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                className={`rounded-xl transition-all duration-200 border-2 ${
                  !selectedCategoryId
                    ? 'shadow-lg scale-105'
                    : 'opacity-80 hover:opacity-100 hover:shadow-md'
                }`}
                style={{
                  borderColor: !selectedCategoryId ? accentColor : 'transparent',
                }}
              >
                <div className="w-20 h-20 sm:w-28 sm:h-28 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${accentColor}15` }}
                >
                  <span className="font-bold text-sm" style={{ color: accentColor }}>
                    {isArabic ? 'الكل' : 'All'}
                  </span>
                </div>
              </button>
              {clinicCategories.map((cat) => {
                const isSelected = selectedCategoryId === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => navigate(`/clinic/${slug}/category/${cat.id}`)}
                    className={`rounded-xl transition-all duration-200 border-2 overflow-hidden ${
                      isSelected
                        ? 'shadow-lg scale-105'
                        : 'opacity-80 hover:opacity-100 hover:shadow-md'
                    }`}
                    style={{
                      borderColor: isSelected ? accentColor : 'transparent',
                    }}
                  >
                    {cat.image_url ? (
                      <div className="w-20 h-20 sm:w-28 sm:h-28 relative">
                        <img src={cat.image_url} alt={cat.name} className="w-full h-full object-cover" />
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                          <span className="text-white text-xs font-bold">{cat.name}</span>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="w-20 h-20 sm:w-28 sm:h-28 flex items-center justify-center"
                        style={{ backgroundColor: `${accentColor}15` }}
                      >
                        <span className="font-bold text-sm px-2 text-center" style={{ color: accentColor }}>
                          {cat.name}
                        </span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </MotionWrapper>
        )}


        {/* Products Section */}
        <MotionWrapper enabled={useAnimations} delay={0.3} className="mb-10" id="products">
          <h3 className={`font-bold text-xl mb-6 text-center ${isDark ? 'text-white' : ''}`}>{isArabic ? 'الباقات المتاحة' : 'Available Packages'}</h3>

          {/* Promotion Banner removed - now shown as ribbons on cards */}



          {products.length === 0 ? (
            <Card className={templateStyles.cardClasses}>
              <CardContent className="py-12 text-center">
                <Package className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'opacity-30'}`} />
                <p className={isDark ? 'text-gray-400' : 'text-muted-foreground'}>{isArabic ? 'لا توجد باقات متاحة حالياً' : 'No packages available currently'}</p>
              </CardContent>
            </Card>
          ) : template.styles.productsLayout === 'list' ? (
            /* List Layout for Elegant template - horizontal rows */
            <div className="space-y-4" dir="ltr">
              {products.map((product, index) => (
                <MotionWrapper enabled={useAnimations} key={product.id} delay={0.1 * index}>
                  <Card
                    className={`relative overflow-hidden ${templateStyles.cardClasses} hover:shadow-lg transition-all duration-300`}
                    style={{ border: template.styles.theme === 'minimal' ? '1px solid #e5e7eb' : undefined }}
                  >
                    {/* Promotion Ribbon */}
                    {(() => {
                      const discount = getProductDiscount(product);
                      if (!discount?.promoName) return null;
                      return (
                        <div 
                          className="absolute -left-8 top-6 z-10 text-white text-xs font-medium py-1 w-32 text-center shadow-md"
                          style={{ 
                            backgroundColor: accentColor, 
                            transform: 'rotate(-45deg)',
                          }}
                        >
                          <Percent className="inline h-3 w-3 ml-1" />
                          {discount.promoName}
                        </div>
                      );
                    })()}
                    <CardContent className="p-4 flex flex-row-reverse sm:flex-row items-center gap-3">
                      {/* Button on LEFT (bottom on mobile) */}
                      {product.max_clients && product.current_clients_count! >= (product.total_slots || product.max_clients) ? (
                        <Button
                          variant="secondary"
                          disabled
                          className="shrink-0 text-xs sm:text-sm"
                        >
                          <Users className="h-4 w-4 ml-1" />
                          اكتمل العدد
                        </Button>
                      ) : (
                        <Button
                          className="text-white shrink-0 text-xs sm:text-sm px-3 sm:px-4"
                          style={{ backgroundColor: accentColor }}
                          onClick={() => handleBookProduct(product)}
                        >
                          {product.product_type === 'paid' ? 'اشترك' : product.product_type === 'digital' ? 'اشتر' : 'احجز'}
                        </Button>
                      )}

                      {/* Content in middle-right */}
                      <div className="text-right flex-1 min-w-0 overflow-hidden">
                        <div className="flex items-center justify-end gap-2 mb-1">
                          <h3 className={`font-bold text-lg min-w-0 break-words ${isDark ? 'text-white' : ''}`}>{product.name_ar}</h3>
                          {product.product_type === 'free_consultation' && (
                            <Badge 
                              className="text-white border-0"
                              style={{ backgroundColor: accentColor }}
                            >
                              <Sparkles className="h-3 w-3 ml-1" />
                              مجانية
                            </Badge>
                          )}
                          {product.product_type === 'paid_consultation' && (
                            <Badge 
                              className="text-white border-0"
                              style={{ backgroundColor: '#f97316' }}
                            >
                              استشارة مدفوعة
                            </Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center justify-end gap-2 text-sm">
                          {(product.product_type === 'paid' || product.product_type === 'paid_consultation' || product.product_type === 'digital') && (() => {
                            const discount = getProductDiscount(product);
                            return discount ? (
                              <span className="font-bold flex items-center gap-2">
                                <span className="line-through opacity-50">{discount.originalPrice}</span>
                                <span style={{ color: accentColor }}>{discount.discountedPrice} ر.س</span>
                                <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: `${accentColor}15`, color: accentColor }}>-{discount.discountLabel}</span>
                              </span>
                            ) : (
                              <span style={{ color: accentColor }} className="font-bold">
                                {product.price} ر.س
                              </span>
                            );
                          })()}
                          {product.subscription_duration_months && product.product_type === 'paid' && (
                            <span className={isDark ? 'text-gray-400' : 'text-muted-foreground'}>
                              {product.subscription_duration_months} شهر
                            </span>
                          )}
                          {product.consultation_duration_minutes && (product.product_type === 'free_consultation' || product.product_type === 'paid_consultation') && (
                            <span className={isDark ? 'text-gray-400' : 'text-muted-foreground'}>
                              {product.consultation_duration_minutes} دقيقة
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Image on RIGHT */}
                      {product.image_url ? (
                        <img 
                          src={product.image_url} 
                          alt={product.name_ar}
                          className="w-16 h-16 rounded-lg object-cover shrink-0"
                        />
                      ) : (
                        <div 
                          className="w-16 h-16 rounded-lg flex items-center justify-center shrink-0"
                          style={{ backgroundColor: `${accentColor}15` }}
                        >
                          <Package className="w-6 h-6 opacity-40" style={{ color: accentColor }} />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </MotionWrapper>
              ))}
            </div>
          ) : (
            /* Grid Layout for other templates */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {products.map((product, index) => (
                <MotionWrapper enabled={useAnimations} key={product.id} delay={0.1 * index}>
                  <Card
                    className={`h-full relative overflow-hidden ${templateStyles.cardClasses} hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer`}
                    onClick={() => setSelectedProduct(product)}
                  >
                    {/* Promotion Ribbon */}
                    {(() => {
                      const discount = getProductDiscount(product);
                      if (!discount?.promoName) return null;
                      return (
                        <div 
                          className="absolute -left-8 top-6 z-10 text-white text-xs font-medium py-1 w-32 text-center shadow-md"
                          style={{ 
                            backgroundColor: accentColor, 
                            transform: 'rotate(-45deg)',
                          }}
                        >
                          <Percent className="inline h-3 w-3 ml-1" />
                          {discount.promoName}
                        </div>
                      );
                    })()}
                    {/* Product Image */}
                    {product.image_url ? (
                      <div className="h-36 sm:h-48 md:aspect-[4/3] md:h-auto bg-white relative overflow-hidden">
                        <img 
                          src={product.image_url} 
                          alt={product.name_ar}
                          className="w-full h-full object-contain p-2"
                        />
                        {product.product_type === 'free_consultation' && (
                          <div className="absolute top-3 right-3">
                            <Badge 
                              className="text-white border-0 shadow-md"
                              style={{ backgroundColor: accentColor }}
                            >
                              <Sparkles className="h-3 w-3 ml-1" />
                              مجانية
                            </Badge>
                          </div>
                        )}
                      </div>
                    ) : (
                      product.product_type === 'free_consultation' && (
                        <div className="absolute top-3 right-3 z-10">
                          <Badge
                            className="text-white border-0 shadow-md"
                            style={{ backgroundColor: accentColor }}
                          >
                            <Sparkles className="h-3 w-3 ml-1" />
                            مجانية
                          </Badge>
                        </div>
                      )
                    )}

                    <CardContent className="p-5 space-y-4 overflow-hidden">
                      {/* Title & Price */}
                      <div className="flex items-start justify-between gap-3">
                        <h3 className={`font-bold text-lg leading-tight min-w-0 break-words ${isDark ? 'text-white' : ''}`}>{product.name_ar}</h3>
                        {(product.product_type === 'paid' || product.product_type === 'paid_consultation' || product.product_type === 'digital') && (() => {
                          const discount = getProductDiscount(product);
                          return discount ? (
                            <div className="text-left shrink-0">
                              <span className="text-sm line-through opacity-50 block">{discount.originalPrice} ر.س</span>
                              <span className="text-2xl font-bold" style={{ color: accentColor }}>{discount.discountedPrice}</span>
                              <span className={isDark ? 'text-gray-400 text-sm' : 'text-muted-foreground text-sm'}> ر.س</span>
                              <span className="block text-xs mt-0.5 px-1.5 py-0.5 rounded w-fit" style={{ backgroundColor: `${accentColor}15`, color: accentColor }}>-{discount.discountLabel}</span>
                            </div>
                          ) : (
                            <div className="text-left shrink-0">
                              <span className="text-2xl font-bold" style={{ color: accentColor }}>{product.price}</span>
                              <span className={isDark ? 'text-gray-400 text-sm' : 'text-muted-foreground text-sm'}> ر.س</span>
                            </div>
                          );
                        })()}
                      </div>

                      {/* Description */}
                      {product.description_ar && (
                        <p className={`text-sm line-clamp-2 break-all overflow-hidden ${isDark ? 'text-gray-400' : 'text-muted-foreground'}`}>
                          {product.description_ar}
                        </p>
                      )}

                      {/* Features */}
                      <div className="space-y-2 py-2">
                        {product.product_type === 'paid' && product.subscription_duration_months && (
                          <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : ''}`}>
                            <CheckCircle className="w-4 h-4" style={{ color: accentColor }} />
                            <span>مدة الاشتراك: {product.subscription_duration_months} شهر</span>
                          </div>
                        )}
                        {(product.product_type === 'free_consultation' || product.product_type === 'paid_consultation') && (
                          <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : ''}`}>
                            <Clock className="w-4 h-4" style={{ color: accentColor }} />
                            <span>مدة الاستشارة: {product.consultation_duration_minutes} دقيقة</span>
                          </div>
                        )}
                        {/* Show remaining slots if max_clients is set */}
                        {product.max_clients && (
                          <>
                            {/* Show "اكتمل العدد" always when full */}
                            {product.current_clients_count! >= (product.total_slots || product.max_clients) ? (
                              <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : ''}`}>
                                <Users className="w-4 h-4" style={{ color: accentColor }} />
                                <span>اكتمل العدد</span>
                              </div>
                            ) : (
                              /* Only show remaining count if show_available_slots is enabled */
                              clinic?.settings?.show_available_slots !== false && (
                                <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : ''}`}>
                                  <Users className="w-4 h-4" style={{ color: accentColor }} />
                                  <span>متبقي {(product.total_slots || product.max_clients) - (product.current_clients_count || 0)} مقعد</span>
                                </div>
                              )
                            )}
                          </>
                        )}
                      </div>

                      {/* CTA Button */}
                      {product.max_clients && product.current_clients_count! >= (product.total_slots || product.max_clients) ? (
                        <Button 
                          className="w-full gap-2"
                          variant="secondary"
                          disabled
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Users className="h-4 w-4" />
                          اكتمل العدد
                        </Button>
                      ) : (
                        <Button 
                          className="w-full text-white gap-2 group-hover:gap-3 transition-all"
                          style={{ backgroundColor: accentColor }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleBookProduct(product);
                          }}
                        >
                          {product.product_type === 'paid' ? 'اشترك الآن' : product.product_type === 'digital' ? 'اشتر الآن' : 'احجز موعدك'}
                          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </MotionWrapper>
              ))}
            </div>
          )}
        </MotionWrapper>


      </div>

        {/* Footer - Same style as DietiTech main footer - Full Width */}
        <MotionWrapper enabled={useAnimations} delay={0.5}>
          <footer 
            className="w-full pt-8 mt-0 bg-muted/40 border-t border-border clinic-footer"
            style={{ 
              fontFamily: selectedFont.fontFamily,
            }}
          >
            <style>{`
              .clinic-footer a:hover {
                color: ${accentColor} !important;
              }
            `}</style>
            <div className="w-full px-4 md:px-8 lg:px-16 xl:px-24">
              {/* Dynamic grid - adjust columns based on available sections */}
              <div className={`grid gap-6 md:gap-8 mb-8 grid-cols-1 ${
                clinic.custom_pages && (clinic.custom_pages.privacy?.enabled || clinic.custom_pages.refund?.enabled)
                  ? 'sm:grid-cols-2 md:grid-cols-4'
                  : 'sm:grid-cols-2 md:grid-cols-3'
              }`}>
              {/* About Clinic */}
              <div>
                <div className="flex items-center gap-2 font-semibold text-foreground mb-4">
                  {clinic.logo_url ? (
                    <img 
                      src={clinic.logo_url} 
                      alt={clinic.clinic_name_ar} 
                      className={`h-10 w-10 object-cover ${
                        template.styles.cardRadius === 'full' ? 'rounded-full' : 
                        template.styles.cardRadius === 'large' ? 'rounded-xl' : 'rounded-lg'
                      }`}
                    />
                  ) : (
                    <div 
                      className={`h-10 w-10 flex items-center justify-center text-white font-bold text-sm ${
                        template.styles.cardRadius === 'full' ? 'rounded-full' : 
                        template.styles.cardRadius === 'large' ? 'rounded-xl' : 'rounded-lg'
                      }`}
                      style={{ backgroundColor: accentColor }}
                    >
                      {clinic.clinic_name_ar?.charAt(0)}
                    </div>
                  )}
                </div>
                {clinic.description_ar && (
                  <p 
                    className="text-sm leading-relaxed text-muted-foreground"
                    style={{ textWrap: 'balance' } as React.CSSProperties}
                  >
                    {clinic.description_ar.substring(0, 100)}...
                  </p>
                )}
              </div>

              {/* Quick Links */}
              <div>
                <h3 className="font-semibold text-foreground mb-4">
                  {isArabic ? 'روابط سريعة' : 'Quick Links'}
                </h3>
                <ul className="space-y-2 text-sm">
                  <li>
                    <Link 
                      to={`/clinic/${slug}`}
                      state={clinicRouteState}
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      {isArabic ? 'الرئيسية' : 'Home'}
                    </Link>
                  </li>
                  {clinic.custom_pages?.testimonials?.enabled && (
                    <li>
                      <Link
                        to={`/clinic/${clinic.slug}/testimonials`}
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        {isArabic ? 'آراء العملاء' : 'Testimonials'}
                      </Link>
                    </li>
                  )}
                  {clinic.custom_pages?.faq?.enabled && (
                    <li>
                      <Link 
                        to="/faq"
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        {isArabic ? 'الأسئلة الشائعة' : 'FAQ'}
                      </Link>
                    </li>
                  )}
                  {clinic.custom_pages?.contact?.enabled && (
                    <li>
                      <Link 
                        to={`/clinic/${slug}/contact`}
                        state={clinicRouteState}
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        {isArabic ? 'تواصل معنا' : 'Contact Us'}
                      </Link>
                    </li>
                  )}
                  {clinic.custom_pages?.about?.enabled && (
                    <li>
                      <Link 
                        to={`/clinic/${slug}/about`}
                        state={clinicRouteState}
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        {isArabic ? 'نبذة عنا' : 'About Us'}
                      </Link>
                    </li>
                  )}
                </ul>
              </div>

              {/* Site Policies - Only show if any policy is enabled */}
              {clinic.custom_pages && (clinic.custom_pages.privacy?.enabled || clinic.custom_pages.refund?.enabled) && (
                <div>
                  <h3 className="font-semibold text-foreground mb-4">
                    {isArabic ? 'سياسات الموقع' : 'Site Policies'}
                  </h3>
                  <ul className="space-y-2 text-sm">
                    {clinic.custom_pages.privacy?.enabled && (
                      <li>
                        <Link 
                          to={`/clinic/${slug}/privacy`}
                          state={clinicRouteState}
                          className="text-muted-foreground hover:text-primary transition-colors"
                        >
                          {isArabic ? 'سياسة الخصوصية وشروط الاستخدام' : 'Privacy Policy & Terms'}
                        </Link>
                      </li>
                    )}
                    {clinic.custom_pages.refund?.enabled && (
                      <li>
                        <Link 
                          to={`/clinic/${slug}/refund`}
                          state={clinicRouteState}
                          className="text-muted-foreground hover:text-primary transition-colors"
                        >
                          {isArabic ? 'سياسة الاسترجاع' : 'Refund Policy'}
                        </Link>
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {/* Contact Info */}
              <div id="contact">
                <h3 className="font-semibold text-foreground mb-4">
                  {isArabic ? 'تواصل معنا' : 'Contact Us'}
                </h3>
                <div className="flex flex-col gap-2 text-sm text-muted-foreground">
                  {clinic.social_links?.instagram && clinic.settings?.show_instagram !== false && (
                    <a href={`https://instagram.com/${clinic.social_links.instagram.replace('@','')}`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 hover:text-primary transition-colors">
                      <svg className="w-4 h-4 flex-shrink-0 text-pink-500" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                      <span className="w-px h-4 bg-border"></span>
                      <span dir="ltr">{clinic.social_links.instagram}</span>
                    </a>
                  )}
                  {clinic.social_links?.twitter && clinic.settings?.show_twitter !== false && (
                    <a href={`https://x.com/${clinic.social_links.twitter.replace('@','')}`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 hover:text-primary transition-colors">
                      <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                      <span className="w-px h-4 bg-border"></span>
                      <span dir="ltr">{clinic.social_links.twitter}</span>
                    </a>
                  )}
                  {clinic.social_links?.tiktok && clinic.settings?.show_tiktok !== false && (
                    <a href={`https://tiktok.com/@${clinic.social_links.tiktok.replace('@','')}`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 hover:text-primary transition-colors">
                      <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>
                      <span className="w-px h-4 bg-border"></span>
                      <span dir="ltr">{clinic.social_links.tiktok}</span>
                    </a>
                  )}
                  {clinic.social_links?.snapchat && clinic.settings?.show_snapchat !== false && (
                    <a href={`https://snapchat.com/add/${clinic.social_links.snapchat.replace('@','')}`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 hover:text-primary transition-colors">
                      <svg className="w-4 h-4 flex-shrink-0 text-yellow-400" viewBox="0 0 24 24" fill="currentColor"><path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12 1.033-.301.165-.088.344-.104.464-.104.182 0 .359.029.509.09.45.149.734.479.734.838.015.449-.39.839-1.213 1.168-.089.029-.209.075-.344.119-.45.135-1.139.36-1.333.81-.09.224-.061.524.12.868l.015.015c.06.136 1.526 3.475 4.791 4.014.255.044.435.27.42.509 0 .075-.015.149-.045.225-.24.569-1.273.988-3.146 1.271-.059.091-.12.375-.164.57-.029.179-.074.36-.134.553-.076.271-.27.405-.555.405h-.03c-.135 0-.313-.031-.538-.074-.36-.075-.765-.135-1.273-.135-.3 0-.599.015-.913.074-.6.104-1.123.464-1.723.884-.853.599-1.826 1.288-3.294 1.288-.06 0-.119-.015-.18-.015h-.149c-1.468 0-2.427-.675-3.279-1.288-.599-.42-1.107-.779-1.707-.884-.314-.045-.629-.074-.928-.074-.509 0-.914.06-1.289.149-.224.045-.39.061-.54.061-.374 0-.523-.224-.583-.42-.061-.192-.09-.389-.135-.567-.046-.181-.105-.494-.166-.57-1.918-.222-2.95-.642-3.189-1.226-.031-.063-.052-.15-.055-.225-.015-.243.165-.465.42-.509 3.264-.54 4.73-3.879 4.791-4.02l.016-.029c.18-.345.224-.645.119-.869-.195-.434-.884-.658-1.332-.809-.121-.029-.24-.074-.346-.119-1.107-.435-1.257-.93-1.197-1.273.09-.479.674-.793 1.168-.793.146 0 .27.029.383.074.42.194.789.3 1.104.3.234 0 .384-.06.465-.105l-.046-.569c-.098-1.626-.225-3.651.307-4.837C7.392 1.077 10.739.807 11.727.807l.419-.015h.06z"/></svg>
                      <span className="w-px h-4 bg-border"></span>
                      <span dir="ltr">{clinic.social_links.snapchat}</span>
                    </a>
                  )}
                  {clinic.contact_email && clinic.settings?.show_email !== false && (
                    <a
                      href={`mailto:${clinic.contact_email}`}
                      className="flex items-center gap-2 hover:text-primary transition-colors"
                    >
                      <Mail className="w-4 h-4 flex-shrink-0" />
                      <span className="w-px h-4 bg-border"></span>
                      <span dir="ltr">{clinic.contact_email}</span>
                    </a>
                  )}
                  {clinic.contact_phone && clinic.settings?.show_phone !== false && (
                    <a
                      href={`tel:${clinic.contact_phone}`}
                      className="flex items-center gap-2 hover:text-primary transition-colors"
                    >
                      <Phone className="w-4 h-4 flex-shrink-0" />
                      <span className="w-px h-4 bg-border"></span>
                      <span dir="ltr">{clinic.contact_phone}</span>
                    </a>
                  )}
                  {clinic.whatsapp_number && clinic.settings?.show_whatsapp !== false && (
                    <a
                      href={`https://wa.me/${clinic.whatsapp_number}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 hover:text-primary transition-colors"
                    >
                      <MessageCircle className="w-4 h-4 flex-shrink-0" />
                      <span className="w-px h-4 bg-border"></span>
                      <span dir="ltr">{clinic.whatsapp_number}</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>

            {/* Copyright */}
            <div className="text-center pt-6 pb-4 px-4 border-t border-border">
              <p className="text-sm text-muted-foreground" style={{ fontFamily: selectedFont.fontFamily }}>
                {isArabic 
                  ? `جميع الحقوق محفوظة © ${new Date().getFullYear()} ${clinic.clinic_name_ar}`
                  : `© ${new Date().getFullYear()} ${clinic.clinic_name_ar}. All Rights Reserved`}
              </p>
              <p className="text-xs mt-2 text-muted-foreground/70" style={{ fontFamily: selectedFont.fontFamily }}>
                {isArabic ? 'عيادة إلكترونية مدعومة من' : 'Electronic clinic powered by'}{' '}
                <a 
                  href="/" 
                  className="font-medium hover:underline text-primary"
                >
                  DietiTech
                </a>
              </p>
            </div>
          </footer>
        </MotionWrapper>
      {/* Product Details Dialog */}
      <Dialog open={!!selectedProduct} onOpenChange={(open) => !open && setSelectedProduct(null)}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-lg max-h-[85vh] overflow-y-auto overflow-x-hidden" dir="rtl" style={{ fontFamily: selectedFont.fontFamily }}>
          {selectedProduct && (
            <>
              {/* Product Image */}
              {selectedProduct.image_url && (
                <div className="w-full h-56 rounded-lg overflow-hidden -mt-2 mb-2">
                  <img 
                    src={selectedProduct.image_url} 
                    alt={selectedProduct.name_ar}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              <DialogHeader>
                <DialogTitle className="text-right text-xl">{selectedProduct.name_ar}</DialogTitle>
              </DialogHeader>

              {/* Price */}
              {(selectedProduct.product_type === 'paid' || selectedProduct.product_type === 'paid_consultation' || selectedProduct.product_type === 'digital') && (() => {
                const discount = getProductDiscount(selectedProduct);
                return discount ? (
                  <div className="flex items-baseline gap-2 justify-end">
                    <span className="text-lg line-through opacity-50">{discount.originalPrice} ر.س</span>
                    <span className="text-3xl font-bold" style={{ color: accentColor }}>{discount.discountedPrice}</span>
                    <span className="text-muted-foreground text-sm">ر.س</span>
                    <span className="text-xs px-2 py-1 rounded font-medium" style={{ backgroundColor: `${accentColor}15`, color: accentColor }}>-{discount.discountLabel}</span>
                  </div>
                ) : (
                  <div className="flex items-baseline gap-1 justify-end">
                    <span className="text-3xl font-bold" style={{ color: accentColor }}>{selectedProduct.price}</span>
                    <span className="text-muted-foreground text-sm">ر.س</span>
                  </div>
                );
              })()}
              {selectedProduct.product_type === 'free_consultation' && (
                <Badge className="text-white border-0 w-fit" style={{ backgroundColor: accentColor }}>
                  <Sparkles className="h-3 w-3 ml-1" />
                  مجانية
                </Badge>
              )}

              {/* Full Description */}
              {selectedProduct.description_ar && (
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap break-all overflow-hidden w-full">
                  {selectedProduct.description_ar}
                </p>
              )}

              {/* Features */}
              <div className="space-y-2 border-t pt-3">
                {selectedProduct.product_type === 'paid' && selectedProduct.subscription_duration_months && (
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4" style={{ color: accentColor }} />
                    <span>مدة الاشتراك: {selectedProduct.subscription_duration_months} شهر</span>
                  </div>
                )}
                {(selectedProduct.product_type === 'free_consultation' || selectedProduct.product_type === 'paid_consultation') && (
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4" style={{ color: accentColor }} />
                    <span>مدة الاستشارة: {selectedProduct.consultation_duration_minutes} دقيقة</span>
                  </div>
                )}
                {selectedProduct.max_clients && (
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="w-4 h-4" style={{ color: accentColor }} />
                    {selectedProduct.current_clients_count! >= (selectedProduct.total_slots || selectedProduct.max_clients) ? (
                      <span>اكتمل العدد</span>
                    ) : clinic?.settings?.show_available_slots !== false ? (
                      <span>متبقي {(selectedProduct.total_slots || selectedProduct.max_clients) - (selectedProduct.current_clients_count || 0)} مقعد</span>
                    ) : null}
                  </div>
                )}
              </div>

              {/* CTA Button */}
              {selectedProduct.max_clients && selectedProduct.current_clients_count! >= (selectedProduct.total_slots || selectedProduct.max_clients) ? (
                <Button className="w-full gap-2" variant="secondary" disabled>
                  <Users className="h-4 w-4" />
                  اكتمل العدد
                </Button>
              ) : (
                <Button 
                  className="w-full text-white gap-2"
                  style={{ backgroundColor: accentColor }}
                  onClick={() => {
                    setSelectedProduct(null);
                    handleBookProduct(selectedProduct);
                  }}
                >
                  {selectedProduct.product_type === 'paid' ? 'اشترك الآن' : selectedProduct.product_type === 'digital' ? 'اشتر الآن' : 'احجز موعدك'}
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClinicPublicPage;
